import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import { WaitingAlert, DangerAlert, Table, InfoAlert, BtnBar } from '@tmapy/style-guide';

import { useSelector } from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { useOrganizationsListResource } from '../../openapi/operations/organizationsList';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { FilterBtn } from '../../app/components/FilterBtn';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';

import { OrganizationListItem } from './OrganizationListItem';
import { OrganizationFilter } from './OrganizationFilter';
import {
  organizationMessages as msg,
  organizationListMessages,
  organizationBtnMessages,
} from './organizationMessages';

export const OrganizationList: React.FC = () => {
  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const filters = useSelector((state) => state.filter.organizationList);

  const query: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }

  const { queryState, data, fetchMore } = useOrganizationsListResource(
    query,
    navigationType !== 'PUSH',
  );

  const organizationList = data?.results ?? [];
  const organizationCount = data?.count;

  function handleNextOrganizationsBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(organizationBtnMessages.filter)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='subjectlist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...organizationListMessages.pageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />

            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
            </BtnBar>
          </header>
          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...organizationListMessages.loadQueryStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...organizationListMessages.loadQueryStateFail} />
            </DangerAlert>
          )}
          {organizationList.length > 0 ? (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage {...msg.name} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.ico} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.dic} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.address} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizationList.map((organizationItem) => (
                      <OrganizationListItem key={organizationItem.id} {...organizationItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={organizationList.length}
                total={organizationCount!}
                queryState={queryState}
                onNextBtnClick={handleNextOrganizationsBtnClick}
              />
            </div>
          ) : (
            <InfoAlert>
              <FormattedMessage {...organizationListMessages.filterQueryStateEmpty} />
            </InfoAlert>
          )}
        </article>
      </MainContent>
      <OrganizationFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
